import { Loader2, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { useDeleteHourlyTuitionDraft } from '~entities/payment/api/hourly-tuition/delete-hourly-tuition-draft';

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '~shared/shadcn/ui/alert-dialog';
import { Button } from '~shared/shadcn/ui/button';

interface DeleteHourlyTuitionDraftDialogProps {
  draftId: string;
  sessionName: string;
  fromDate: string;
  toDate: string;
}

const formatBillingDate = (dateString: string) =>
  new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

export function DeleteHourlyTuitionDraftDialog({
  draftId,
  sessionName,
  fromDate,
  toDate,
}: DeleteHourlyTuitionDraftDialogProps) {
  const { mutateAsync: deleteDraft, isPending } = useDeleteHourlyTuitionDraft();
  const [open, setOpen] = useState(false);

  const billingPeriod = `${formatBillingDate(fromDate)} - ${formatBillingDate(toDate)}`;

  const handleDelete = async () => {
    try {
      await deleteDraft({ draftId });
      toast.success('Hourly tuition draft deleted successfully.');
      setOpen(false);
    } catch (error) {
      console.error('Error deleting hourly tuition draft:', error);
      toast.error(
        error instanceof Error
          ? error.message
          : 'Failed to delete the draft. Please try again.',
      );
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        <Button size='sm' variant='destructive'>
          <Trash2 />
          Delete
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete Hourly Tuition Draft</AlertDialogTitle>
          <AlertDialogDescription>
            Delete the draft for <strong>{sessionName}</strong> ({billingPeriod}
            )? This removes the saved tuition draft and cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPending}>Cancel</AlertDialogCancel>
          <Button
            onClick={() => {
              void handleDelete();
            }}
            disabled={isPending}
            variant='destructive'
          >
            {isPending ? (
              <>
                <Loader2 className='size-4 animate-spin' />
                Deleting...
              </>
            ) : (
              'Delete Draft'
            )}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
